import type { AppState } from './models';
import type { WorldSpaceId } from './worldSpaces';

export type FirstWeekStageId = 'observe' | 'choose' | 'understand' | 'learn' | 'explore' | 'connect' | 'reflect';

export interface FirstWeekStageDefinition {
  id: FirstWeekStageId;
  day: number;
  title: string;
  shortTitle: string;
  copy: string;
  actionLabel: string;
  route: string;
  spaceId: WorldSpaceId;
  emotion: string;
  rewardXp: number;
  icon: string;
  test(state: AppState): boolean;
}

export interface FirstWeekStageState extends FirstWeekStageDefinition {
  completed: boolean;
  unlocked: boolean;
  current: boolean;
}

export interface FirstWeekJourney {
  stages: FirstWeekStageState[];
  completedCount: number;
  percent: number;
  currentStage: FirstWeekStageState;
  complete: boolean;
  summary: string;
}

const A = '/assets/ui-icons';

export const FIRST_WEEK_STAGES: FirstWeekStageDefinition[] = [
  {
    id: 'observe', day: 1, title: '내 돈을 바라보는 첫눈', shortTitle: '돈 기록',
    copy: '받고, 쓰고, 모은 돈 중 하나를 직접 기록하며 내 돈의 흐름을 발견해요.',
    actionLabel: '첫 돈 기록 남기기', route: '/child/records', spaceId: 'basecamp', emotion: '호기심', rewardXp: 20,
    icon: `${A}/mrm_ui_01a_record_v01.png`, test: (state) => state.records.length > 0,
  },
  {
    id: 'choose', day: 2, title: '나에게 맞는 선택 찾기', shortTitle: '선택 미션',
    copy: '소비·저축·나눔 중 한 가지를 골라 선택의 이유를 행동으로 남겨요.',
    actionLabel: '선택 미션 시작', route: '/child/missions', spaceId: 'market', emotion: '통제감', rewardXp: 30,
    icon: `${A}/mrm_ui_12a_mission_v01.png`, test: (state) => state.missions.some((mission) => ['submitted', 'approved'].includes(mission.status)) || state.records.some((record) => record.type === 'saving'),
  },
  {
    id: 'understand', day: 3, title: '경제의 날씨 읽기', shortTitle: '경제 퀴즈',
    copy: '오늘의 경제 소식을 한 줄로 읽고 숫자 뒤에 있는 이유를 찾아요.',
    actionLabel: '경제기사 퀴즈 풀기', route: '/child/concepts/economy', spaceId: 'news', emotion: '이해', rewardXp: 40,
    icon: `${A}/mrm_ui_12d_concept_v01.png`, test: (state) => state.quizAttempts.length > 0,
  },
  {
    id: 'learn', day: 4, title: '사라진 지식 페이지 복원', shortTitle: '개념 학습',
    copy: '경제도서관의 이야기 한 장을 끝까지 읽고 개념을 현실의 선택과 연결해요.',
    actionLabel: '개념 한 장 배우기', route: '/child/concepts', spaceId: 'library', emotion: '유능감', rewardXp: 50,
    icon: `${A}/mrm_ui_07_missiontip_v01.png`, test: (state) => state.learningProgress.some((progress) => progress.completed),
  },
  {
    id: 'explore', day: 5, title: '경제가 움직이는 길 발견', shortTitle: '현실 탐험',
    copy: '시장·항구·금융기관처럼 경제가 실제로 움직이는 장소를 관찰해요.',
    actionLabel: '금융 패스포트 탐험', route: '/child/passport', spaceId: 'arctic-port', emotion: '확장감', rewardXp: 60,
    icon: `${A}/mrm_ui_02e_targetdate_v01.png`, test: (state) => state.passportVisits.some((visit) => ['submitted', 'approved'].includes(visit.status)),
  },
  {
    id: 'connect', day: 6, title: '가족 탐험대와 나누기', shortTitle: '가족 대화',
    copy: '내 선택을 평가받기보다 가족과 이유를 이야기하고 응원을 기억으로 남겨요.',
    actionLabel: '가족 미션 이어가기', route: '/child/family-missions', spaceId: 'fridge', emotion: '연결감', rewardXp: 80,
    icon: `${A}/mrm_ui_08_familytalk_v01.png`, test: (state) => state.familyMissionChallenges.some((mission) => ['submitted', 'approved', 'completed'].includes(mission.status)) || state.messages.length > 0,
  },
  {
    id: 'reflect', day: 7, title: '첫 주의 작은 정상', shortTitle: '성장 회고',
    copy: '일주일 동안 달라진 선택을 돌아보고 수료증과 다음 작은 정상을 확인해요.',
    actionLabel: '첫 주 성장 돌아보기', route: '/child/hall-of-honor', spaceId: 'observatory', emotion: '자부심', rewardXp: 120,
    icon: `${A}/mrm_ui_09_report_v01.png`, test: (state) => state.growthSummary.currentStreak >= 7 || state.growthSummary.completedBoardDays >= 7 || state.badges.length >= 3,
  },
];

export function deriveFirstWeekJourney(state: AppState): FirstWeekJourney {
  const completedFlags = FIRST_WEEK_STAGES.map((stage) => stage.test(state));
  const firstIncomplete = completedFlags.findIndex((completed) => !completed);
  const currentIndex = firstIncomplete === -1 ? FIRST_WEEK_STAGES.length - 1 : firstIncomplete;
  const stages = FIRST_WEEK_STAGES.map((stage, index) => ({
    ...stage,
    completed: completedFlags[index],
    unlocked: index === 0 || completedFlags.slice(0, index).every(Boolean),
    current: index === currentIndex,
  }));
  const completedCount = completedFlags.filter(Boolean).length;
  const complete = completedCount === FIRST_WEEK_STAGES.length;
  const currentStage = stages[currentIndex];
  return {
    stages,
    completedCount,
    percent: Math.round((completedCount / FIRST_WEEK_STAGES.length) * 100),
    currentStage,
    complete,
    summary: complete
      ? '첫 주의 일곱 걸음을 모두 완성했어요. 다음 작은 정상을 직접 정해 보세요.'
      : `${currentStage.day}일차 ${currentStage.shortTitle}을 마치면 다음 공간이 더 선명해져요.`,
  };
}

export function firstWeekStageForSpace(state: AppState, spaceId: WorldSpaceId): FirstWeekStageState | undefined {
  return deriveFirstWeekJourney(state).stages.find((stage) => stage.spaceId === spaceId);
}
